import PropTypes from 'prop-types';
import {useMemo, useState, useEffect} from 'react';
import {useDebouncedEffect} from '@ozgene/gp5.hooks.use-debounced-effect';
import {TextField, Grid} from '@material-ui/core';
import {getStringChangeHandler} from '@ozgene/gp5.utils.data-functions';
import {
  validateUniqueConsumableName,
  validateUniqueConsumableProductCode,
} from 'Consumables/service/ConsumableService';

export default function ConsumableEditor(props) {
  const {entity, onPropertyChange, onValidityChange} = props;
  const {id = '', name = '', supplier = '', productCode = ''} = entity;
  const [isValidating, setIsValidating] = useState(false);
  const [nameErrorText, setNameErrorText] = useState('');
  const [productCodeErrorText, setProductCodeErrorText] = useState('');
  const handleChangeName = getStringChangeHandler('name', onPropertyChange);
  const handleChangeProductCode = getStringChangeHandler(
    'productCode',
    onPropertyChange,
  );
  const handleChangeSupplier = getStringChangeHandler(
    'supplier',
    onPropertyChange,
  );
  const isNameInvalid = useMemo(
    () => !name || !!nameErrorText,
    [name, nameErrorText],
  );
  const isProductCodeInvalid = useMemo(
    () => !productCode || !!productCodeErrorText,
    [productCode, productCodeErrorText],
  );
  const supplierErrorMessage = useMemo(
    () => (!supplier ? 'Supplier is required' : ''),
    [supplier],
  );

  useDebouncedEffect(
    () => {
      const validateName = async () => {
        setNameErrorText(await validateUniqueConsumableName(name, id));
        setIsValidating(false);
      };

      return validateName();
    },
    [name],
    500,
  );
  useDebouncedEffect(
    () => {
      const validateProductCode = async () => {
        setProductCodeErrorText(
          await validateUniqueConsumableProductCode(productCode, id),
        );
        setIsValidating(false);
      };

      return validateProductCode();
    },
    [productCode],
    500,
  );
  useEffect(() => {
    setIsValidating(true);
  }, [name, productCode]);
  useEffect(
    () =>
      onValidityChange(
        !isValidating && !isNameInvalid && !isProductCodeInvalid && supplier,
      ),
    [
      isValidating,
      isNameInvalid,
      onValidityChange,
      supplier,
      isProductCodeInvalid,
    ],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Name"
          type="text"
          value={name}
          onChange={handleChangeName}
          error={isNameInvalid}
          helperText={nameErrorText}
          inputProps={{maxLength: 200}}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Product code"
          type="text"
          value={productCode}
          onChange={handleChangeProductCode}
          error={isProductCodeInvalid}
          helperText={productCodeErrorText}
          inputProps={{maxLength: 45}}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Supplier"
          type="text"
          value={supplier}
          onChange={handleChangeSupplier}
          error={!supplier}
          helperText={supplierErrorMessage}
          inputProps={{maxLength: 45}}
        />
      </Grid>
    </Grid>
  );
}

ConsumableEditor.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    productCode: PropTypes.string,
    supplier: PropTypes.string,
  }),
  onPropertyChange: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func.isRequired,
};
